import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import jwtDecode from 'jwt-decode';
import AuthLayout from './auth/AuthLayout';
import { Banner, Field } from '../design/ui';

// Landing of the Shopify app install: a new merchant completes the account
// (username + password), an existing one is sent to the app.
const Welcome = () => {
  const { t } = useTranslation();
  const [userDetails, setUserDetails] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserDetails(decodedToken);
        setIsNewUser(decodedToken.is_new_user || false);
      } catch (decodeError) {
        console.error('Invalid welcome token');
        navigate('/'); // Redirect if the token is invalid
      }
    } else {
      navigate('/'); // Redirect if no token is found in URL
    }
  }, [navigate]);

  const handleRegistration = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    formData.append('token', new URLSearchParams(window.location.search).get('token'));
    try {
      const response = await fetch('/api/shopify/shopify/complete-registration', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error('Registration failed');
      }
      await response.json();
      window.location.href = 'https://unanimity.ai';
    } catch (registrationError) {
      console.error('Registration error:', registrationError.message);
      setError(t('publicUi.registrationFailed', 'L’inscription a échoué. Réessayez.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartClick = () => {
    window.location.href = 'https://unanimity.ai';
  };

  return (
    <AuthLayout
      heading={isNewUser ? t('publicUi.completeRegistration', 'Terminer l’inscription') : t('publicUi.thanks', 'Merci de votre confiance')}
      subheading={isNewUser
        ? t('publicUi.completeRegistrationSub', 'Choisissez l’identifiant et le mot de passe du compte.')
        : t('publicUi.credentialsSent', 'Vos identifiants vous ont été envoyés par e-mail ; vous pourrez les changer une fois connecté.')}
    >
      {isNewUser ? (
        <form className="u-form" onSubmit={handleRegistration}>
          {error && <Banner tone="danger">{error}</Banner>}
          <Field label={t('publicUi.username', 'Identifiant')} htmlFor="welcome-username">
            <input id="welcome-username" type="text" className="u-input" value={username} onChange={(e) => setUsername(e.target.value)} minLength="3" autoComplete="username" required />
          </Field>
          <Field label={t('password', 'Mot de passe')} htmlFor="welcome-password" help={t('publicUi.passwordRule6', '6 caractères minimum.')}>
            <input id="welcome-password" type="password" className="u-input" value={password} onChange={(e) => setPassword(e.target.value)} minLength="6" autoComplete="new-password" required />
          </Field>
          <button type="submit" className="u-btn u-btn--primary u-btn--block" disabled={isLoading}>
            {isLoading ? t('publicUi.registering', 'Inscription…') : t('publicUi.completeRegistration', 'Terminer l’inscription')}
          </button>
        </form>
      ) : (
        <div className="u-form">
          {userDetails && (
            <Banner tone="neutral">
              {t('publicUi.yourEmail', 'Votre e-mail : {{email}}', { email: userDetails.sub })}
            </Banner>
          )}
          <p className="u-help">{t('publicUi.feedback', 'Une idée, une fonctionnalité qui manque ? Écrivez-nous, nous lisons tout.')}</p>
          {userDetails && (
            <button type="button" onClick={handleStartClick} className="u-btn u-btn--primary u-btn--block">
              {t('publicUi.start', 'Commencer')}
            </button>
          )}
        </div>
      )}
    </AuthLayout>
  );
};

export default Welcome;
